import { useState, useEffect, useRef, useCallback } from "react";
import { MdWarning, MdAdd, MdDelete } from "react-icons/md";
import { FiTrash, FiPlus } from "react-icons/fi";
import { Modal, ModalButton } from "./Modal";
import { api } from "../services/api";
import { toast } from "react-toastify";

interface ModalEmergencyObservationProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface Customer {
  id: string;
  name: string;
}

export function ModalEmergencyObservation({
  isOpen,
  onClose,
  onSuccess,
}: ModalEmergencyObservationProps) {
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [observations, setObservations] = useState<string[]>([""]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const searchCustomers = useCallback(async (term: string = "") => {
    setLoading(true);
    try {
      const response = await api.get(`/costumers/search?name=${term}`);
      setCustomers(response.data);
      setShowDropdown(true);
    } catch (err) {
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load all customers on mount
  useEffect(() => {
    if (isOpen) {
      searchCustomers("");
    }
  }, [isOpen, searchCustomers]);

  // Debounce search
  useEffect(() => {
    if (!showDropdown) return undefined;

    const timer = setTimeout(() => {
      searchCustomers(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchCustomers, showDropdown]);

  // Handle clicks outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCustomerSelect = (customer: Customer) => {
    setSelectedCustomerId(customer.id);
    setSelectedCustomerName(customer.name);
    setSearchTerm(customer.name);
    setShowDropdown(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value) {
      setSelectedCustomerId("");
      setSelectedCustomerName("");
    }
  };

  const handleAddObservation = () => {
    setObservations([...observations, ""]);
  };

  const handleRemoveObservation = (index: number) => {
    if (observations.length > 1) {
      const newObservations = observations.filter((_, i) => i !== index);
      setObservations(newObservations);
    }
  };

  const handleObservationChange = (index: number, value: string) => {
    const newObservations = [...observations];
    newObservations[index] = value;
    setObservations(newObservations);
  };

  const handleSubmit = async () => {
    // Validações
    if (!selectedCustomerId) {
      toast.error("Selecione um cliente");
      return;
    }

    const validObservations = observations.filter(obs => obs.trim() !== "");

    if (validObservations.length === 0) {
      toast.error("Adicione ao menos uma observação");
      return;
    }

    setIsSubmitting(true);

    try {
      await api.post("/costumer-observations/bulk", {
        costumer_id: selectedCustomerId,
        texts: validObservations,
      });

      toast.success(
        `${validObservations.length} observaç${validObservations.length > 1 ? "ões" : "ão"} emergencial criada${validObservations.length > 1 ? "s" : ""} com sucesso!`
      );

      // Reset form
      setSelectedCustomerId("");
      setSelectedCustomerName("");
      setSearchTerm("");
      setObservations([""]);
      setShowDropdown(false);

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
        "Erro ao criar observações emergenciais"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedCustomerId("");
      setSelectedCustomerName("");
      setSearchTerm("");
      setObservations([""]);
      setShowDropdown(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Observação Emergencial"
      icon={<MdWarning size={24} />}
      variant="danger"
      maxWidth="max-w-2xl"
      footer={
        <>
          <ModalButton variant="cancel" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </ModalButton>
          <ModalButton
            variant="danger"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Salvando..." : "Salvar Observações"}
          </ModalButton>
        </>
      }
    >
      <div className="space-y-6">
        {/* Seleção de Cliente */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Cliente <span className="text-red-500">*</span>
          </label>
          <div className="relative w-full">
            <input
              ref={inputRef}
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={() => {
                setShowDropdown(true);
                if (customers.length === 0 && !loading) {
                  searchCustomers(searchTerm);
                }
              }}
              className="h-[52px] px-4 w-full rounded-xl text-gray-700 bg-white border border-gray-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
              autoComplete="off"
              disabled={isSubmitting}
            />

            {loading && (
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin h-4 w-4 border-2 border-red-500 border-t-transparent rounded-full" />
              </div>
            )}

            {showDropdown && customers.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              >
                {customers.map((customer) => (
                  <button
                    type="button"
                    key={customer.id}
                    onClick={() => handleCustomerSelect(customer)}
                    className="w-full text-left px-4 py-3 hover:bg-red-50 cursor-pointer text-gray-700 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    {customer.name}
                  </button>
                ))}
              </div>
            )}

            {showDropdown &&
              searchTerm.length > 0 &&
              customers.length === 0 &&
              !loading && (
                <div
                  ref={dropdownRef}
                  className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg"
                >
                  <div className="px-4 py-3 text-gray-500 text-center">
                    Nenhum cliente encontrado
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Observações */}
        <div className="p-4 rounded-xl border-2 border-dashed border-red-300 bg-gradient-to-br from-red-50 to-orange-50">
          <div className="flex items-center gap-2 mb-2">
            <div className="p-1.5 bg-red-100 rounded-lg">
              <MdWarning size={16} className="text-red-600" />
            </div>
            <span className="text-sm font-semibold text-red-600">
              Observações <span className="text-red-500">*</span>
            </span>
          </div>
          <p className="text-xs text-gray-600 mb-4">
            Adicione observações que requerem atenção urgente. O cliente será
            marcado com status de emergência.
          </p>

          <div className="space-y-3">
            {observations.map((observation, index) => (
              <div
                key={`obs-${index}`}
                className="flex gap-2 items-start"
              >
                <textarea
                  placeholder={`Observação ${index + 1}...`}
                  value={observation}
                  onChange={(e) => handleObservationChange(index, e.target.value)}
                  className="flex-1 h-[70px] p-3 rounded-xl text-gray-700 bg-white border border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-500/20 resize-none text-sm transition-all duration-200"
                  disabled={isSubmitting}
                />
                {observations.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveObservation(index)}
                    className="p-2.5 text-red-500 hover:bg-red-100 rounded-xl transition-colors disabled:opacity-50"
                    title="Remover observação"
                    disabled={isSubmitting}
                  >
                    <FiTrash size={18} />
                  </button>
                )}
              </div>
            ))}

            <button
              type="button"
              onClick={handleAddObservation}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 border-2 border-red-300 rounded-xl hover:bg-red-100 transition-all duration-200 text-sm font-semibold disabled:opacity-50"
              disabled={isSubmitting}
            >
              <FiPlus size={16} />
              Adicionar Observação
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
